import { Component } from '@theme/component';
import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Chromium-only network/device hints; elsewhere they are absent and the video plays.
 * @returns {{ saveData?: boolean, effectiveType?: string } | undefined}
 */
const connection = () => /** @type {any} */ (navigator).connection;

/**
 * Contexts that get the poster only: Save-Data, a 2G/3G effective connection, or a device with
 * 2 GB of memory or less. CPU cores are deliberately not used — `hardwareConcurrency` is clamped
 * by some browsers for privacy, so it would switch the video off on capable phones.
 * @returns {boolean}
 */
const prefersPosterOnly = () => {
  const net = connection();
  if (net?.saveData) return true;
  if (['slow-2g', '2g', '3g'].includes(net?.effectiveType ?? '')) return true;
  return Number(/** @type {any} */ (navigator).deviceMemory) <= 2;
};

/** How much a background video may be upscaled before it visibly softens. */
const UPSCALE_TOLERANCE = 1.15;
/** Device pixel ratio cap for choosing a rendition — a background video gains nothing visible beyond 2x. */
const MAX_PIXEL_RATIO = 2;
/** Ping-pong gives up when the median reverse seek is slower than this (~12 fps). */
const REVERSE_SEEK_BUDGET_MS = 80;
/** Reverse seeks sampled before judging whether this device keeps up. */
const REVERSE_SEEK_SAMPLES = 12;

/**
 * Hero with per-breakpoint image/video media (sections/sl-hero-media.liquid).
 *
 * The poster <picture> is the LCP element and is never touched here. This component only
 * decides whether — and which — background video plays over it:
 * - nothing is requested before window `load` + idle, so the video never competes with the LCP;
 * - only the current breakpoint's MP4 is attached, and only while the hero is on screen and the
 *   tab is visible. data-sources-desktop / data-sources-mobile list every rendition up to that
 *   breakpoint's cap as `url@height` (snippets/sl-video-mp4-url.liquid); the smallest one that
 *   covers the pixels the hero paints wins. A rendition that fails to load is skipped, and
 *   data-fallback-* (the URL Liquid lists, when it is over the cap) is the last resort;
 * - reduced motion, Save-Data, a slow connection, a low-memory device, a blocked autoplay (iOS Low
 *   Power Mode) or running out of candidates leave the poster in place and download nothing more.
 *
 * data-loop="ping-pong" plays forward, then backwards, then forward again. No browser except
 * Safari can play a video backwards, so the reverse leg is a chain of seeks, each issued only
 * after the previous one has completed — measured on Shopify's renditions (keyframe every 3 s) at
 * 15–37 seeks/s depending on resolution, with no long tasks. A device whose median seek is slower than
 * REVERSE_SEEK_BUDGET_MS falls back to a plain loop for the rest of the visit.
 * Why each rule exists, with the measurements: docs/plans/HOME_HERO_MEDIA.md.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} [video] - The background video. Absent on image-only heroes.
 * @property {HTMLButtonElement} [toggle] - The pause/play control (WCAG 2.2.2).
 *
 * @extends {Component<Refs>}
 */
export class SlHeroMediaComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  #ready = false;
  #visible = false;
  #allowed = false;
  #userPaused = false;
  #blocked = false;
  #currentSrc = '';
  /** URLs that failed to load; the next candidate is tried instead. */
  #failed = new Set();
  /** @type {{ large: boolean, src: string } | null} The source chosen for the current breakpoint. */
  #picked = null;

  #pingPong = false;
  /** @type {'forward' | 'reverse'} */
  #direction = 'forward';
  /** Incremented on every reverse start, so a stale `seeked` from an older chain is ignored. */
  #chain = 0;
  #reverseFrom = 0;
  #reverseStartedAt = 0;
  /** @type {number[]} */
  #seekSamples = [];

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  /**
   * Section Rendering API / theme editor re-render. morph.js drops attributes the server did not
   * send — including the src this component set — so start over from a clean state.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Pause / resume. Public on purpose: on:click="/togglePlayback" calls instance[method], so a
   * #private method would never fire.
   */
  togglePlayback() {
    if (!this.#video) return;

    this.#userPaused = !this.#userPaused;
    this.#sync();
  }

  #setup() {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    this.#video = video;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // The attribute alone is not always reflected after a morph; play() needs the property.
    video.muted = true;
    this.#pingPong = video.dataset.loop === 'ping-pong';
    video.loop = !this.#pingPong;

    // Media events do not bubble, so component.js' delegated on:* handlers cannot see them.
    video.addEventListener('playing', this.#onPlaying, { signal });
    video.addEventListener('ended', this.#onEnded, { signal });
    video.addEventListener('error', this.#onError, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });
    mediaQueryLarge.addEventListener('change', this.#sync, { signal });
    reducedMotion.addEventListener('change', this.#sync, { signal });

    // A play() started from JS does not get the automatic off-screen pause that the autoplay
    // attribute gets, so visibility is tracked here.
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    });
    this.#observer.observe(this);

    this.#whenIdleAfterLoad(signal, () => {
      this.#ready = true;
      this.#sync();
    });
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#detach();
    this.#video = null;
    this.#picked = null;
    this.#seekSamples = [];
    this.#ready = false;
    this.#visible = false;
    this.#allowed = false;
    this.classList.remove('is--playing');
    this.#updateToggle();
  }

  /**
   * Runs `callback` once the page has loaded and the main thread is idle. Guarded by the signal
   * instead of cancelIdleCallback, because the Safari fallback is setTimeout.
   * @param {AbortSignal} signal
   * @param {() => void} callback
   */
  #whenIdleAfterLoad(signal, callback) {
    const idle = () =>
      requestIdleCallback(
        () => {
          if (!signal.aborted) callback();
        },
        { timeout: 2000 }
      );

    if (document.readyState === 'complete') {
      idle();
    } else {
      window.addEventListener('load', idle, { once: true, signal });
    }
  }

  /**
   * The source for the current breakpoint, chosen once per breakpoint so a resize or a scroll
   * never swaps a rendition mid-play; re-chosen only when the breakpoint changes or it fails.
   * @returns {string}
   */
  #pickSource() {
    const large = mediaQueryLarge.matches;
    if (this.#picked?.large === large && !this.#failed.has(this.#picked.src)) return this.#picked.src;

    const src = this.#chooseRendition(large);
    this.#picked = { large, src };
    return src;
  }

  /**
   * The smallest rendition whose width covers the device pixels the hero paints, else the largest
   * one left; the fallback URL only once every candidate has failed.
   * @param {boolean} large
   * @returns {string}
   */
  #chooseRendition(large) {
    const video = this.#video;
    if (!video) return '';

    const data = video.dataset;
    const tokens = (large ? data.sourcesDesktop : data.sourcesMobile) ?? '';
    const fallback = (large ? data.fallbackDesktop : data.fallbackMobile) ?? '';
    const ratio = Number(large ? data.ratioDesktop : data.ratioMobile) || 16 / 9;

    const renditions = tokens
      .split(' ')
      .filter(Boolean)
      .map((token) => {
        const at = token.lastIndexOf('@');
        return { url: token.slice(0, at), height: Number(token.slice(at + 1)) };
      })
      .filter((rendition) => rendition.url !== '' && !this.#failed.has(rendition.url));

    const needed = this.#paintedDevicePixels(ratio);
    const enough = renditions.find((rendition) => rendition.height * ratio * UPSCALE_TOLERANCE >= needed);
    const choice = enough ?? renditions.at(-1);
    if (choice) return choice.url;

    return fallback !== '' && !this.#failed.has(fallback) ? fallback : '';
  }

  /**
   * Width, in device pixels, that the video paints with object-fit: cover — the box width, or more
   * when the box is proportionally taller than the video.
   * @param {number} ratio - The video's aspect ratio.
   * @returns {number}
   */
  #paintedDevicePixels(ratio) {
    const { width, height } = this.getBoundingClientRect();
    return Math.max(width, height * ratio) * Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  }

  /** Reconciles the video with the current breakpoint, visibility and user intent. */
  #sync = () => {
    const video = this.#video;
    if (!video) return;

    const src = this.#ready ? this.#pickSource() : '';
    this.#allowed = src !== '' && !this.#blocked && !reducedMotion.matches && !prefersPosterOnly();

    if (!this.#allowed) {
      this.#reset();
      return;
    }

    // Breakpoint swap: abort the other breakpoint's download before attaching this one.
    if (this.#currentSrc && this.#currentSrc !== src) this.#reset();

    if (!this.#canAnimate()) {
      video.pause();
      this.#updateToggle();
      return;
    }

    if (!this.#currentSrc) this.#attach(src);
    this.#play();
    this.#updateToggle();
  };

  /** @returns {boolean} */
  #canAnimate() {
    return this.#allowed && this.#visible && !document.hidden && !this.#userPaused;
  }

  /** @param {string} src */
  #attach(src) {
    const video = this.#video;
    if (!video) return;

    // With preload="none", play() on this page sometimes sends no request at all
    // (assets/sl-custom.js startVideo), so raise it on the one video about to play.
    video.preload = 'auto';
    video.src = src;
    this.#currentSrc = src;
  }

  /** pause() does not stop a download — only dropping the source and calling load() aborts it. */
  #detach() {
    this.#direction = 'forward';
    this.#chain++;

    const video = this.#video;
    if (!video || !this.#currentSrc) return;

    video.pause();
    video.removeAttribute('src');
    video.preload = 'none';
    video.load();
    this.#currentSrc = '';
  }

  #play() {
    const video = this.#video;
    if (!video) return;

    if (this.#direction === 'reverse') {
      this.#startReverse();
      return;
    }
    if (!video.paused) return;

    video.play()?.catch((error) => {
      // AbortError means a later pause()/load() superseded this play() — not a failure.
      if (error?.name !== 'NotAllowedError') return;

      // Autoplay refused (iOS Low Power Mode): keep the poster and stop downloading.
      this.#blocked = true;
      this.#sync();
    });
  }

  /** Ping-pong: the forward leg reached the end, so walk back to the start. */
  #onEnded = () => {
    if (!this.#pingPong) return;

    this.#direction = 'reverse';
    this.#startReverse();
  };

  /** Starts (or resumes, after a pause) the reverse leg from wherever the video is now. */
  #startReverse() {
    const video = this.#video;
    if (!video) return;

    this.#chain++;
    this.#reverseFrom = video.currentTime;
    this.#reverseStartedAt = performance.now();
    this.#stepReverse(this.#chain);
  }

  /**
   * One reverse step. The target follows the wall clock, so the reverse leg lasts as long as the
   * forward one however fast the decoder is; the next seek is only issued once this one has
   * completed, so seeks never pile up — a slow device shows fewer frames instead of falling behind.
   * @param {number} chain
   */
  #stepReverse(chain) {
    const video = this.#video;
    if (!video || chain !== this.#chain || this.#direction !== 'reverse' || !this.#canAnimate()) return;

    const target = this.#reverseFrom - (performance.now() - this.#reverseStartedAt) / 1000;
    if (target <= 0) {
      this.#direction = 'forward';
      video.currentTime = 0;
      this.#play();
      return;
    }

    const issuedAt = performance.now();
    video.addEventListener(
      'seeked',
      () => {
        if (chain !== this.#chain) return;
        if (!this.#keepsUp(performance.now() - issuedAt)) return;
        // Straight on, not after a requestAnimationFrame: the seeked frame stays on screen until
        // the next one is decoded, and on Shopify's 3 s keyframe interval the decoder, not the
        // display, is the bottleneck — waiting a frame only cost reverse frames.
        this.#stepReverse(chain);
      },
      { once: true, signal: this.#controller?.signal }
    );
    video.currentTime = target;
  }

  /**
   * Samples the first reverse seeks; when the median is over budget this device cannot play the
   * reverse leg smoothly, so ping-pong is switched off for the visit and the video loops instead.
   * @param {number} ms
   * @returns {boolean} Whether to keep reversing.
   */
  #keepsUp(ms) {
    if (this.#seekSamples.length >= REVERSE_SEEK_SAMPLES) return true;

    this.#seekSamples.push(ms);
    if (this.#seekSamples.length < REVERSE_SEEK_SAMPLES) return true;

    const sorted = [...this.#seekSamples].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    if (median <= REVERSE_SEEK_BUDGET_MS) return true;

    const video = this.#video;
    this.#pingPong = false;
    this.#direction = 'forward';
    this.#chain++;
    if (video) {
      video.loop = true;
      video.currentTime = 0;
    }
    this.#play();
    return false;
  }

  #reset() {
    this.#detach();
    this.classList.remove('is--playing');
    this.#updateToggle();
  }

  #onPlaying = () => {
    this.classList.add('is--playing');
    this.#updateToggle();
  };

  /** The derived lower rendition can 404 (its naming is not a documented contract): try the next. */
  #onError = () => {
    if (!this.#currentSrc) return;

    this.#failed.add(this.#currentSrc);
    this.#reset();
    this.#sync();
  };

  /**
   * The control is only shown while there is a video to control, so it never sits as a dead
   * button over a still poster. aria-pressed carries the state; the label stays "Pause video".
   */
  #updateToggle() {
    const { toggle } = this.refs;
    if (!(toggle instanceof HTMLButtonElement)) return;

    toggle.hidden = !(this.#allowed && (this.classList.contains('is--playing') || this.#userPaused));
    toggle.setAttribute('aria-pressed', String(this.#userPaused));
  }
}

if (!customElements.get('sl-hero-media-component')) {
  customElements.define('sl-hero-media-component', SlHeroMediaComponent);
}
